import { BRAND_COLORS } from '~/components/brand/brandTokens';

import Core from './core';
import type { OperationalResult, OperationalRow } from './types';

// Approved operational design exceptions: semantic status pairs and sans-only typography.
export const UI = {
  teal: `var(--flash-teal, ${BRAND_COLORS.oceanTeal})`,
  header: BRAND_COLORS.oceanTeal,
  sand: `var(--flash-sand, ${BRAND_COLORS.sandyBeige})`,
  paper: `var(--flash-paper, ${BRAND_COLORS.compassWhite})`,
  white: BRAND_COLORS.compassWhite,
  ink: `var(--flash-ink, ${BRAND_COLORS.jetBlack})`,
  mint: BRAND_COLORS.coralGreen,
  line: 'var(--flash-line, #EBE7DE)',
  muted: 'var(--flash-muted, rgba(15,12,8,.68))',
  below: { background: 'var(--flash-below-bg, #FBE0E0)', color: 'var(--flash-below-text, #8C1D1D)' },
  in_line: { background: 'var(--flash-inline-bg, #F3F1EA)', color: 'var(--flash-inline-text, #0F464F)' },
  above: { background: 'var(--flash-above-bg, #D5FCE2)', color: 'var(--flash-above-text, #0B5B36)' },
  unscored: { background: 'var(--flash-unscored-bg, #EBE7DE)', color: 'var(--flash-unscored-text, #5E5E5E)' },
};
export const cardSx = { bgcolor: UI.paper, border: `1px solid ${UI.line}`, borderRadius: 2, p: 2, minWidth: 0 };
export const tableSx = {
  '& .MuiTableCell-root': { fontSize: 13, py: 1, px: 1, borderColor: UI.line, lineHeight: 1.3 },
  '& .MuiTableCell-head': { bgcolor: UI.header, color: UI.white, whiteSpace: 'nowrap', fontWeight: 500 },
};
export const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
export function value(value: number | null | undefined, unit = '') {
  if (!finite(value)) return 'Unknown';
  if (['percent', 'ratio'].includes(unit))
    return `${(value * 100).toLocaleString('en-AU', { maximumFractionDigits: 2 })}%`;
  if (/currency|AUD/.test(unit))
    return `${value < 0 ? '-' : ''}A$${Math.abs(value).toLocaleString('en-AU', { maximumFractionDigits: Math.abs(value) >= 1000 ? 0 : 2 })}`;
  return value.toLocaleString('en-AU', { maximumFractionDigits: Math.abs(value) >= 1000 ? 0 : 1 });
}
export function moneyShort(amount: number | null | undefined) {
  if (!finite(amount)) return 'Unknown';
  return `${amount < 0 ? '-' : ''}A$${Math.abs(amount) >= 1e6 ? (Math.abs(amount) / 1e6).toFixed(2) + 'M' : Math.abs(amount) >= 1000 ? Math.round(Math.abs(amount) / 1000) + 'k' : Math.round(Math.abs(amount))}`;
}
export function dateShort(day: string | undefined) {
  if (!day) return '—';
  return new Date(day + 'T00:00:00Z').toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Australia/Sydney',
  });
}
export const stageDefinitions = [
  { id: 'impressions_per_day', label: 'Impressions', unit: '', basis: 'per campaign-day' },
  { id: 'views_per_day', label: 'Offer views', unit: '', basis: 'per campaign-day' },
  { id: 'packages_per_day', label: 'Packages', unit: '', basis: 'per campaign-day' },
  { id: 'margin_per_day', label: 'Margin', unit: 'currency', basis: 'A$ per campaign-day' },
];
export const stepDefinitions = [
  { id: 'impressions_to_views', label: 'Impressions to offer views', unit: 'percent' },
  { id: 'views_to_packages', label: 'Offer views to packages', unit: 'percent' },
  { id: 'margin_per_view', label: 'Margin per offer view', unit: 'currency' },
];
export function downloadCsv(csv: string, name: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Linear within-stage comparison. The larger nonnegative value fills the slot. */
export function funnelWidths(actual: number | null, median: number | null, slot: number) {
  if (!finite(actual) || !finite(median) || !finite(slot) || slot < 0) return { actualWidth: null, medianWidth: null };
  const current = Math.max(0, actual),
    peer = Math.max(0, median),
    maximum = Math.max(current, peer);
  return { actualWidth: maximum ? slot * (current / maximum) : 0, medianWidth: maximum ? slot * (peer / maximum) : 0 };
}

/** Advanced fields must remain visible after navigation preserves their URL scope. */
export function advancedFilterSummary(filters: Partial<import('./types').FlashFilters>) {
  const labels = {
    search: 'Search',
    owner: 'Market manager',
    campaignType: 'Type',
    launchFrom: 'Launch from',
    launchTo: 'Launch through',
    observationFrom: 'Observe from',
    observationTo: 'Observe through',
    ageMin: 'Minimum age',
    ageMax: 'Maximum age',
    weeklyExclusion: 'Weekly exclusions',
  };
  return (Object.keys(labels) as Array<keyof typeof labels>)
    .filter(
      (key) =>
        !!filters[key] &&
        (key !== 'weeklyExclusion' || (filters.view === 'portfolio' && filters.historyView === 'weekly')),
    )
    .map((key) => ({
      key,
      label: labels[key],
      value:
        key === 'weeklyExclusion' ? `${Core.exclusionIds(filters.weeklyExclusion).length} campaigns` : filters[key],
    }));
}

/** Review priority requires enough observation and a below-quartile margin rate. */
export function reviewPriorities<T extends Pick<OperationalRow, 'comparable' | 'tooEarly' | 'positions'>>(
  rows: T[],
): T[] {
  return rows.filter((row) => row.comparable && !row.tooEarly && row.positions.margin_per_day === 'below');
}

export function changeText(change: number | null | undefined) {
  if (!finite(change)) return 'unavailable';
  if (change === 0) return 'unchanged';
  return `${change < 0 ? 'down' : 'up'} ${Math.abs(change * 100).toLocaleString('en-AU', { maximumFractionDigits: 1 })}%`;
}

/** One displayed narrative shared by the page and its assistant context. */
export function portfolioNarrative(result: OperationalResult) {
  if (result.filters.historyView === 'previous') {
    const metric = result.previousRuns.summary.metrics.margin_per_day;
    return [
      metric?.pairCount
        ? `Across ${metric.pairCount} paired ${metric.pairCount === 1 ? 'campaign' : 'campaigns'}, margin/day is ${value(metric.current, 'currency')} v ${value(metric.prior, 'currency')} last Flash.`
        : result.filters.observationFrom || result.filters.observationTo
          ? 'Clear observation dates to compare the same launch days with the previous Flash run. The peer benchmark still uses the selected observation dates.'
          : 'A previous Flash comparison is unavailable for this scope. Check the matching coverage below; missing history does not mean this is the first run.',
      `Peer benchmark: ${result.headline.count} campaigns below pace · ${moneyShort(result.headline.amount)} rate gap (up to 28 days).`,
    ];
  }
  if (!result.coverage.compared) return ['No campaigns have a peer group under these filters.'];
  const hero = result.story.hero;
  const destinations = result.headline.topDestinations
    .slice(0, 2)
    .map((d) => d.name)
    .join(' and ');
  return [
    result.story.available
      ? `Flash margin${result.weekly.exclusionIds.length ? ' with exclusions' : ''} is ${moneyShort(hero.currentWeeklyMargin)} a week, ${hero.direction === 'down' ? 'down' : 'up'} ${value(Math.abs(hero.changePct || 0), 'percent')}.`
      : result.story.reason,
    `Peer benchmark: ${result.headline.count} campaigns below pace · ${moneyShort(result.headline.amount)} rate gap (up to 28 days).${destinations ? ' ' + destinations + ' carry ' + Math.round((result.headline.top2Share || 0) * 100) + '%.' : ''}`,
  ];
}
